import React, { lazy, Suspense } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';
import PropTypes from 'prop-types';

import { encodeSlug } from 'Helpers/Slug';
import FanFavouritesSection from 'Containers/HomePage/FanFavouritesSection';
import FlashDealsSection from 'Containers/AlgoliaSections/FlashDealsSection';
import GamesSection from 'Containers/AlgoliaSections/GamesSection';
import GamesUnderBanner from 'Components/Sales/GamesUnderBanner';
import GeneralUtils from 'Helpers/GeneralUtils';
import GenreLinks from 'Components/QuickLinks/GenreLinks';
import MidPageBannerContainer from 'Components/Banner/MidPageBanner/MidPageBannerContainer';
import MysteryBundleCardContainer from 'Containers/Cards/MysteryBundleCardContainer';
import ProductCarouselSection from 'Containers/HomePage/ProductCarouselSection';
import PublisherCarousel from 'Components/HomePage/PublisherCarousel';
import SaleButton from 'Components/Utility/SaleButton';
import TopFranchises from 'Components/QuickLinks/TopFranchises';
import WishlistMostWantedSection from 'Containers/HomePage/WishlistMostWantedSection';
import pricing from 'Containers/StaticSearch/games-under-pricing';
import MetacriticNewReleasesContainer from 'Containers/ProductLists/MetacriticNewReleasesContainer';
import MostWantedNewReleasesContainer from 'Containers/ProductLists/MostWantedNewReleasesContainer';

import 'Components/OnSale/OnSale.scss';

const BundleFestCountdown = lazy(() => import(/* webpackChunkName: "BundleFestCountdown" */ 'Components/Banner/BundleFestCountdown/BundleFestCountdown'));
const ProductFinder = lazy(() => import(/* webpackChunkName: "ProductFinder" */ 'Components/ProductFinder/ProductFinder'));
const TrustBanner = lazy(() => import(/* webpackChunkName: "TrustBanner" */ 'Components/HomePage/TrustBanner'));

const messages = defineMessages({
  latestDeals: {
    id: 'onsale.onsaletemplate.heading.latestDeals',
    defaultMessage: 'Latest Deals',
  },
  newAndUpcoming: {
    id: 'onsale.onsaletemplate.heading.newAndUpcoming',
    defaultMessage: 'New and Upcoming',
  },
  moreThanJustGames: {
    id: 'onsale.onsaletemplate.heading.moreThanJustGames',
    defaultMessage: 'More Than Just Games',
  },
  dealsUnder: {
    id: 'onsale.onsaletemplate.heading.dealsUnder',
    defaultMessage: 'Deals Under {price}',
  },
  dollarCollections: {
    id: 'onsale.onsaletemplate.heading.dollarCollections',
    defaultMessage: 'Dollar Collections',
  },
  games: {
    id: 'onsale.onsaletemplate.heading.games',
    defaultMessage: 'Games',
  },
  gameBundles: {
    id: 'onsale.onsaletemplate.heading.gameBundles',
    defaultMessage: 'Game Bundles',
  },
  questVrDeals: {
    id: 'onsale.onsaletemplate.heading.questVrDeals',
    defaultMessage: 'Quest VR Deals',
  },
  steamDeckCompatibleGames: {
    id: 'onsale.onsaletemplate.heading.steamDeckCompatibleGames',
    defaultMessage: 'Steam Deck Compatible Games',
  },
});

const OnSaleTemplate = ({ algoliaData }) => {
  const { formatMessage } = useIntl();

  const {
    topSellers,
    gameBundles,
    newReleases,
    ebooks,
    mostWanted,
    dollarCollections,
    publishers,
    steamDeck,
    questVrDeals,
  } = algoliaData;


  const formatPublisherTitle = publisher => `${publisher}${publisher.toLowerCase().includes('games') ? '' : ` ${formatMessage(messages.games)}`}`;
  const newUser = GeneralUtils.newUser();

  return (
    <React.Fragment>
      <section className="container">
        <GamesSection isSaleLive hits={topSellers} slidesToShow={3} />
      </section>

      {newUser &&
        <Suspense>
          <TrustBanner />
        </Suspense>
      }

      <FlashDealsSection noPadding useTimer threeProductTemplate />

      <MysteryBundleCardContainer />

      <Suspense>
        <BundleFestCountdown />
      </Suspense>

      <ProductCarouselSection
        title={formatMessage(messages.gameBundles)}
        viewAllLocation={'bundle'}
        loadedHits={gameBundles}
        viewAllTitle={formatMessage(messages.gameBundles)}
        hitCardNameTag="h3"
        slidesToShow={3}
        lgSize={4}
        itemListTitle="PC Game Bundles"
      />

      <ProductCarouselSection
        title={formatMessage(messages.steamDeckCompatibleGames)}
        viewAllLocation={'steam-deck-games'}
        loadedHits={steamDeck}
        viewAllTitle={formatMessage(messages.steamDeckCompatibleGames)}
        hitCardNameTag="h3"
        itemListTitle="Steam Deck Compatible Games"
      />

      <ProductCarouselSection
        title={formatMessage(messages.questVrDeals)}
        viewAllLocation={'vr-games/oculus-meta-quest/oculus-meta-quest-two'}
        loadedHits={questVrDeals}
        viewAllTitle={formatMessage(messages.questVrDeals)}
        hitCardNameTag="h3"
        itemListTitle="Quest VR Deals"
      />

      <section className="container">
        <div className="games-under-banners section-margin three-banners">
          <GamesUnderBanner
            type="left"
            appearsOn="on_sale"
            to="games-under/5"
            priceUnder={pricing['5'].max}
          />
          <GamesUnderBanner
            type="middle"
            appearsOn="on_sale"
            to="games-under/10"
            priceUnder={pricing['10'].max}
          />
          <GamesUnderBanner
            type="right"
            appearsOn="on_sale"
            to="games-under/20"
            priceUnder={pricing['20'].max}
          />
        </div>
      </section>

      {
        dollarCollections &&
        !!dollarCollections.length &&
        dollarCollections.length > 3 &&
        <ProductCarouselSection
          title={formatMessage(messages.dollarCollections)}
          viewAllLocation={'collections/dollar-collections'}
          loadedHits={dollarCollections}
          viewAllTitle={formatMessage(messages.dollarCollections)}
          hitCardNameTag="h3"
          itemListTitle="Dollar Collections"
        />
      }

      <ProductCarouselSection
        title={formatMessage(messages.newAndUpcoming)}
        viewAllLocation={'new-releases'}
        loadedHits={newReleases}
        viewAllTitle={formatMessage(messages.newAndUpcoming)}
        slidesToShow={3}
        hitCardNameTag="h3"
        itemListTitle="New and Upcoming"
      />

      <MidPageBannerContainer appearsOn="on_sale" />

      <section className="container">
        <GenreLinks headingTag="h2" />
      </section>

      <TopFranchises />

      {!newUser &&
        <WishlistMostWantedSection loadedHits={mostWanted} hitCardNameTag="h3" />
      }

      <FanFavouritesSection onSalePage />

      <Suspense>
        <ProductFinder onSalePage />
      </Suspense>

      <PublisherCarousel showDealNumbers />

      <ProductCarouselSection
        title={formatMessage(messages.moreThanJustGames)}
        viewAllLocation={'bundle/books'}
        loadedHits={ebooks}
        viewAllTitle={formatMessage(messages.moreThanJustGames)}
        hitCardNameTag="h3"
        itemListTitle="More Than Just Games"
      />

      <section className="container">
        <div className="row">
          <div className="col-lg-6 mb-5 mb-lg-0">
            <MetacriticNewReleasesContainer listCardNameTag="h3" hitsToDisplay={5} />
          </div>
          <div className="col-lg-6">
            <MostWantedNewReleasesContainer listCardNameTag="h3" loadedHits={mostWanted} hitsToDisplay={5} />
          </div>
        </div>
      </section>


      {!isEmpty(publishers) && publishers.map(publisher =>
        <ProductCarouselSection
          sectionCss={`section-margin-carousel ${publisher.name}`}
          title={formatPublisherTitle(startCase(publisher.name))}
          viewAllLocation={`publishers/${encodeSlug(startCase(publisher.name))}`}
          loadedHits={publisher.hits}
          viewAllTitle={formatPublisherTitle(startCase(publisher.name))}
          hitCardNameTag="h3"
          itemListTitle={`${startCase(publisher.name)}${publisher.name.toLowerCase().includes('games') ? '' : ' Games'}`}
        />,
      )}

      <section className="container d-flex justify-content-center">
        <SaleButton moreDeals buttonLink={'search'} search={'onSale=true'} />
      </section>

    </React.Fragment>
  );
};


OnSaleTemplate.propTypes = {
  algoliaData: PropTypes.shape({}),
};

OnSaleTemplate.defaultProps = {
  algoliaData: null,
};

export default OnSaleTemplate;
